import { Fragment, useMemo } from 'react';
import type { WireInventoryProjection } from '../../api/endpoints.js';
import type { useApiQuery } from '../../api/hooks.js';
import {
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  TBody,
  TD,
  TH,
  THead,
  TRow,
  Table,
} from '../../ui/index.js';

/**
 * A domain with soft line-break opportunities after each "." and "-", so a
 * narrow column wraps a long domain at a label boundary instead of at an
 * arbitrary letter, and its minimum width is its longest label (the table
 * then fits a tablet without shredding the short domains). Text content is
 * unchanged.
 */
function DomainText({ domain }: { readonly domain: string }): JSX.Element {
  const parts = domain.split(/(?<=[.-])/).filter((p) => p !== '');
  return (
    <span>
      {parts.map((p, i) => (
        <Fragment key={i}>
          {i > 0 ? <wbr /> : null}
          {p}
        </Fragment>
      ))}
    </span>
  );
}

interface AggregatedDomain {
  domain: string;
  availableCount: number;
  reservedCount: number;
  leasedCount: number;
  usedCount: number;
  disabledCount: number;
  totalImported: number;
  accountCount: number;
}

export function InventoryPanel({
  query,
  onRefresh,
}: {
  readonly query: ReturnType<typeof useApiQuery<{ projections: readonly WireInventoryProjection[] }>>;
  readonly onRefresh: () => void;
}): JSX.Element {
  const aggregate = useMemo(() => {
    if (query.kind !== 'ok') return [] as readonly AggregatedDomain[];
    const projections = query.data?.projections ?? [];
    const byDomain = new Map<string, AggregatedDomain>();
    for (const p of projections) {
      const cur = byDomain.get(p.domain) ?? {
        domain: p.domain,
        availableCount: 0,
        reservedCount: 0,
        leasedCount: 0,
        usedCount: 0,
        disabledCount: 0,
        totalImported: 0,
        accountCount: 0,
      };
      cur.availableCount += p.availableCount;
      cur.reservedCount += p.reservedCount;
      cur.leasedCount += p.leasedCount;
      cur.usedCount += p.usedCount;
      cur.disabledCount += p.disabledCount;
      cur.totalImported += p.totalImported;
      cur.accountCount += 1;
      byDomain.set(p.domain, cur);
    }
    return Array.from(byDomain.values()).sort((a, b) => b.totalImported - a.totalImported);
  }, [query]);

  return (
    <Card flush className="mcp-inventory">
      <CardHeader
        title="Stok Özeti (Domain Bazlı)"
        actions={
          <Button variant="ghost" size="sm" onClick={onRefresh} icon={<Icon.Refresh size={12} />}>
            Yenile
          </Button>
        }
      />
      <CardBody>
        <p className="t-tertiary t-small" style={{ margin: '0 0 12px' }}>
          provider_inventory_projection tablosundan domain başına toplulaştırılmış özet. Parent başına detay için
          parent kartından "Alias" butonuna basın.
        </p>

        {query.kind === 'loading' ? <LoadingState /> : null}
        {query.kind === 'error' ? <ErrorState reason={query.reason} onRetry={onRefresh} /> : null}
        {query.kind === 'ok' ? (
          aggregate.length === 0 ? (
            <EmptyState label="Henüz envanter yok." hint="Alias içe aktarınca otomatik dolar." />
          ) : (
            <Table dense stack scroll className="mcp-inv-table" data-testid="mailcom-inventory-aggregate">
              <THead>
                <TRow>
                  <TH>Domain</TH>
                  <TH>Parent</TH>
                  <TH>Satışa uygun</TH>
                  <TH>Rezerve</TH>
                  <TH>Siparişte</TH>
                  <TH>Kullanıldı</TH>
                  <TH>Devre dışı</TH>
                  <TH>Toplam</TH>
                </TRow>
              </THead>
              <TBody>
                {aggregate.map((d) => (
                  <TRow key={d.domain}>
                    <TD className="t-mono tbl-stack-primary" label="Domain"><DomainText domain={d.domain} /></TD>
                    <TD className="t-mono t-num" label="Parent">{d.accountCount.toLocaleString('tr-TR')}</TD>
                    <TD className="t-mono t-num" label="Satışa uygun">{d.availableCount.toLocaleString('tr-TR')}</TD>
                    <TD className="t-mono t-num" label="Rezerve">{d.reservedCount.toLocaleString('tr-TR')}</TD>
                    <TD className="t-mono t-num" label="Siparişte">{d.leasedCount.toLocaleString('tr-TR')}</TD>
                    <TD className="t-mono t-num" label="Kullanıldı">{d.usedCount.toLocaleString('tr-TR')}</TD>
                    <TD className="t-mono t-num" label="Devre dışı">{d.disabledCount.toLocaleString('tr-TR')}</TD>
                    <TD className="t-mono t-num" label="Toplam">{d.totalImported.toLocaleString('tr-TR')}</TD>
                  </TRow>
                ))}
              </TBody>
            </Table>
          )
        ) : null}
      </CardBody>
    </Card>
  );
}

export function InboxOtpPanel(): JSX.Element {
  return (
    <Card flush>
      <CardHeader title="Inbox ve OTP İzleme" />
      <CardBody>
        <p className="t-tertiary t-small" style={{ margin: '0 0 12px' }}>
          Mesaj gövdeleri ve raw OTP içeriği bu yüzeyde tutulmaz; yalnızca sipariş çıktısının teslim olup
          olmadığı ve son inbox fetch meta-verisi gözlenir. Müşteri yüzeyine yalnızca onaylı OTP iletilir.
        </p>
        <Banner tone="info">
          Inbox/OTP detayı ilgili siparişin <em>Sipariş Detayı</em> sayfasında, delivery timeline alanında
          safe metadata olarak gözükür. Provider olayları aşağıdaki Provider Olayları panelinde izlenebilir
          (event_type=<wbr /><code>MailMessageReceived</code>).
        </Banner>
      </CardBody>
    </Card>
  );
}
